import React, { useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useLocationCtx } from "@/context/LocationContext";
import { LocateFixed, Loader2, Search } from "lucide-react";
import { api } from "@/lib/api";

const CITIES = {
  milano: [45.4642, 9.19], pesaro: [43.9096, 12.9131], roma: [41.9028, 12.4964],
  torino: [45.0703, 7.6869], napoli: [40.8518, 14.2681], firenze: [43.7696, 11.2558],
  bologna: [44.4949, 11.3426], rimini: [44.0678, 12.5695], ancona: [43.6158, 13.5189],
  fano: [43.8436, 13.0189], urbino: [43.7262, 12.6365], bergamo: [45.6983, 9.6773],
};
const RADII = [5, 10, 15, 25, 50];

export default function LocationSheet({ open, onOpenChange }) {
  const { loc, requestGeo, locating, setRadius, setManual } = useLocationCtx();
  const [q, setQ] = useState("");
  const [searching, setSearching] = useState(false);

  const searchCity = async () => {
    const key = q.trim().toLowerCase();
    if (!key) return;
    if (CITIES[key]) {
      setManual(CITIES[key][0], CITIES[key][1], q.trim());
      onOpenChange(false);
      return;
    }
    setSearching(true);
    try {
      const r = await api.post("/maps/autocomplete", { input: q.trim() });
      const first = (r.data?.predictions || r.data?.suggestions || [])[0];
      if (first?.place_id) {
        const pd = await api.get(`/maps/place/${first.place_id}`);
        if (pd.data?.lat) { setManual(pd.data.lat, pd.data.lng, q.trim()); onOpenChange(false); return; }
      }
    } catch (_) {}
    // fallback: keep current
    setSearching(false);
    onOpenChange(false);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="rounded-t-3xl max-w-3xl mx-auto" data-testid="location-sheet">
        <SheetHeader className="text-left">
          <SheetTitle className="text-xl font-extrabold">La tua zona</SheetTitle>
        </SheetHeader>

        <div className="py-4 space-y-5">
          <Button
            onClick={requestGeo}
            disabled={locating}
            data-testid="use-my-location"
            className="w-full h-12 rounded-xl font-bold gap-2"
          >
            {locating ? <Loader2 className="h-5 w-5 animate-spin" /> : <LocateFixed className="h-5 w-5" />}
            Usa la mia posizione
          </Button>

          <div className="flex gap-2">
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && searchCity()}
              placeholder="Cerca città (es. Pesaro)"
              data-testid="city-search-input"
              className="h-12 rounded-xl"
            />
            <Button onClick={searchCity} disabled={searching} variant="outline" className="h-12 w-12 rounded-xl p-0" data-testid="city-search-btn">
              {searching ? <Loader2 className="h-5 w-5 animate-spin" /> : <Search className="h-5 w-5" />}
            </Button>
          </div>

          <div className="flex flex-wrap gap-2">
            {Object.keys(CITIES).map((c) => (
              <button
                key={c}
                onClick={() => { setManual(CITIES[c][0], CITIES[c][1], c[0].toUpperCase() + c.slice(1)); onOpenChange(false); }}
                className="px-3 py-1.5 rounded-full bg-slate-100 hover:bg-[#FFE4E6] hover:text-[#BE123C] text-sm font-semibold capitalize transition-colors"
              >
                {c}
              </button>
            ))}
          </div>

          <div>
            <p className="text-sm font-bold text-slate-700 mb-2">Raggio di ricerca</p>
            <div className="flex gap-2">
              {RADII.map((r) => (
                <button
                  key={r}
                  data-testid={`radius-${r}`}
                  onClick={() => setRadius(r)}
                  className={`flex-1 py-2.5 rounded-xl text-sm font-bold transition-colors ${
                    loc.radius === r ? "kn-gradient text-white" : "bg-slate-100 text-slate-600"
                  }`}
                >
                  {r} km
                </button>
              ))}
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
